import React, { useEffect, useState } from "react";
import {
  Box,
  Center,
  Flex,
  Text,
  Button,
  useDisclosure,
  useMediaQuery,
  Grid,
} from "@chakra-ui/react";
import PuffLoader from "react-spinners/PuffLoader";
import TweetsReplies from "../tweets/tweetsReplies";
import { useSession } from "next-auth/react";
import { getAccount, updateUser } from "../../utils/sessionHelper";
import { useStats } from "hooks/useStats";
import { useOnboardingSteps } from "components/onboard-panel/useOnboardingSteps";
import { formatMedia } from "utils/linkedinUtils";
import { grow } from "../../controllers/search";
import { MainContext } from "../../context/mainContext";
import { useFirebaseUser } from "../../utils/useFirebaseUser";
import { ConfirmPopup } from "components/popups/confirmPopup";
import toast from "react-hot-toast";
import * as analytics from "utils/analytics";
import { textStyle, variant } from "theme/names";
import { UpgradeToProCard } from "components/upgrade-to-pro-card";
import { FilterSortRefreshAutoLike } from "./filter-sort-refresh-autolike-panel";
import { ENGAGE_FILTER_KEY } from "./utils";
import { getLikersAndCommenters } from "controllers/list";

const defaultFiltersValues = {
  defaultMinFollowers: 0,
  defaultMaxFollowers: 999999999,
};

let startLimitationUTCHour = 12;
let endLimitationUTCHour = 19;
let utcHours = (new Date()).getUTCHours();
var isPeakHour = utcHours >= startLimitationUTCHour && utcHours < endLimitationUTCHour;

export function WithMyCircle({header = null as any }) {
  const { defaultMinFollowers, defaultMaxFollowers } = defaultFiltersValues;
  const { data: session, status } = useSession() ?? {}
  const loading = status === "loading"
  const [isPersonalizedSorting, setIsPersonalizedSorting] = React.useState(true);
  const [savedTweet, setSavedTweet] = React.useState([]);
  const [recos, setRecos] = React.useState([]);
  const [isSending, setIsSending] = React.useState(false);
  const [data, setData] = React.useState<any>(null);
  const [nbMinDaysOld, setNbMinDaysOld] = React.useState(0);
  const [nbMaxDaysOld, setNbMaxDaysOld] = React.useState(2);
  const [nbMinLikes, setNbMinLikes] = React.useState(40);
  const [nbMinRt, setNbMinRt] = React.useState(0);
  const [nbMinFollowers, setNbMinFollowers] = React.useState(defaultMinFollowers);
  const [nbMaxFollowers, setNbMaxFollowers] = React.useState(defaultMaxFollowers);
  const [hasCustomFilters, setHasCustomFilters] = useState<boolean>(false);
  const [hasFetched, setHasFetched] = React.useState(false);
  const [noEngage, setNoEngage] = useState<boolean>(false);
  const [listId, setListId] = useState<string>("");
  const [filters, setFilters] = useState<any>(defaultFiltersValues);
  const [refresh, refresher] = React.useState({});
  const [isTooSmall] = useMediaQuery("(max-width: 800px)");
  const { updateStatsCounter } = useStats();
  const { updateOnboardSteps } = useOnboardingSteps();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const mainContext = React.useContext(MainContext);
  const isAuthenticated = useFirebaseUser();
  let isValidToken = getAccount(session)?.thWriteAccessToken;

  // prefill saved filters from localstorage
  useEffect(() => {
    const savedFilters = localStorage.getItem(ENGAGE_FILTER_KEY);
    if (savedFilters) {
      const filtersJSON = JSON.parse(savedFilters);
      const { minFollowers, maxFollowers, minDaysOld, maxDaysOld } = filtersJSON;
      setNbMinDaysOld(minDaysOld || nbMinDaysOld);
      setNbMaxDaysOld(maxDaysOld || nbMaxDaysOld);
      setNbMinFollowers(minFollowers);
      setNbMaxFollowers(maxFollowers);
      setFilters(filtersJSON);
    }
  }, []);

  // track filter changes
  useEffect(() => {
    if (
      (nbMinFollowers !== defaultMinFollowers) ||
      (nbMaxFollowers !== defaultMaxFollowers)
    ) {
      setHasCustomFilters(true);
    }
    else {
      setHasCustomFilters(false);
    }
  }, [nbMinFollowers, nbMaxFollowers]);

  useEffect(() => {
    if (isAuthenticated && !hasFetched && isValidToken) {
      setHasFetched(true);
      sendRequest(session);
      
      updateStatsCounter("access_engage");
    }
  }, [session, isAuthenticated]);

  // const sendRequest = React.useCallback(async (session, topic) => {
  const sendRequest = async (session) => {

    if (isSending) return;

    

    setIsSending(true);

    //set grow limits and sort
    const planLimit = (getAccount(session)?.subscription?.plan != "enterprise")? 10 : 40;
    const peopleInList = await getLikersAndCommenters(getAccount(session)?.id, 100, planLimit);
    
    let getFilters: any = localStorage.getItem(ENGAGE_FILTER_KEY);
    
    if(getFilters) getFilters = JSON.parse(getFilters);

    const params: any = {
      isPersonalizedSorting: isPersonalizedSorting,
      nbMinDaysOld: getFilters?.minDaysOld || nbMinDaysOld,
      nbMaxDaysOld: getFilters?.maxDaysOld || nbMaxDaysOld,
      nbMinLikes: nbMinLikes,
      nbMinRt: nbMinRt,
      nbMinFollowers: getFilters?.minFollowers || defaultMinFollowers,
      nbMaxFollowers: getFilters?.maxFollowers || defaultMaxFollowers,
      noHashtags: getFilters?.noHashtags || false,
      notLiked: getFilters?.notLiked || false,
      noMediaAndLinks: getFilters?.noMediaAndLinks || false,
      positiveKeywords: getFilters?.positiveKeywords || [],
      negativeKeywords: getFilters?.negativeKeywords || [],
      count: 50,
      mode: "people",
      org: getAccount(session)?.subscription?.org,
      engageAccounts: peopleInList.map((contact) => contact.username).slice(0,planLimit).join(",") || "",
    };

    //@ts-ignore
    const data = await grow(session, params, mainContext.onOpenUpgrade);

    if (!data || (data.success === 0 && !data.tweets)) {
      setData(null);
    }
    else {
      data.tweets.forEach(tweet => {
        // tweet.refComposer = React.createRef();
        tweet.isPosting = false;
        formatMedia(tweet);
      });

      setData(data);
    }

    updateOnboardSteps("engage");
    setIsSending(false);
  };

  const onSort = (sortedData) => {
    
    if(sortedData?.length) {
      const finalSortedData = {
        ...data,
        tweets: [...sortedData],
      };
  
      setData(finalSortedData);
    }
    
  };

  const onFilter = (filterData) => {
    setFilters(filterData);
    onRefresh([]);
  }

  const onRefresh = async (sortedFilteredData) => {
    setData(null); //reset data on refresh;
    sendRequest &&
      await sendRequest(
        session,
        //@ts-ignore
        getAccount(session)?.keywords?.join(",")
      );
  }

  useEffect(() => {
    if (listId !== "" && getAccount(session)?.thWriteAccessToken) {
      sendRequest(session);
    }
  }, [listId]);

  return (
    <Grid maxW="800px" margin="auto">
      <Flex
        textStyle={textStyle["body.medium.light"]}
        alignItems="center"
      >
        {
          header ?? (
            <Flex>
              <Text
                textStyle={textStyle["body.medium.light"]}
                fontSize="md"
              >
                Engage on content from other Taplio users to grow faster together
              </Text>
            </Flex>
          )
        }

      </Flex>
      <FilterSortRefreshAutoLike 
        session={session}
        isAuthenticated={isAuthenticated}
        data={data?.tweets}
        onSortCallback={onSort}
        onFilterCallback={onFilter}
        mode={"people"}
        onRefreshButtonClickCallBack={onRefresh}
        filters={filters}
        isLoadingPosts={isSending}
      />
      {isSending && (
        <Center pt={10}>
          <PuffLoader
            color={"#0A66C2"}
            loading={isSending}
            cssOverride={{
              display: "block",
              margin: "0 auto",
              borderColor: "red",
            }}
            size={50}
          />
        </Center>
      )}
      {!isSending && isValidToken && (
        <Flex flexDirection="column">
          {
            //@ts-ignore
            data?.tweets?.length ? (
              <Box
                pt="3"
                w="100%"
                maxW="800px"
              >
                <Center>
                  <TweetsReplies
                    data={data}
                    setData={setData}
                    refresher={refresher}
                  />
                </Center>
              </Box>
            ) : null
          }
          {!data?.tweets?.length && (
            <Center pt={10}>
              <Flex flexDirection="column" alignItems="center">
                <Text textStyle={textStyle["body.medium.light"]}>
                  No LinkedIn posts found matching your filters. Try adjusting your filters or refreshing to find more posts to engage with from your circle.
                </Text>
              </Flex>
            </Center>
          )}
        </Flex>
      )}
      <ConfirmPopup
        isOpen={isOpen}
        onClose={onClose}
        title={noEngage ? "Opt in engage" : "Opt out from engage"}
        body={
          noEngage
            ? "This will add your account in the list of posts to engage with so more people can engage with your content."
            : "This will remove your account from the list of posts to engage with so you will not take any benefit from this extra engagement."
        }
        callback={async () => {
          setNoEngage(!noEngage);
          await updateUser(session, { noEngage: !noEngage });
          toast.success("settings updated");
        }}
      />
    </Grid>
  );
}
